import { Component, OnInit, OnDestroy, NgZone, ApplicationRef, inject } from '@angular/core';

import { NotificationStateService, Notification } from '../state/notification.state';
import { effect, EffectRef } from '@angular/core';

@Component({
  selector: 'app-notification-state',
  template: `
    <div class="notifications-container">
      @for (notification of displayedNotifications(); track notification.id) {
        <div class="notification" [class]="'notification notification-' + notification.type">
          <div class="notification-content">
            <div class="notification-message">{{ notification.message }}</div>
            <button class="notification-close" (click)="remove(notification.id)">×</button>
          </div>
          <div class="notification-progress">
            <div
              class="notification-progress-bar"
              [style.width.%]="getProgressValue(notification)"
            ></div>
          </div>
        </div>
      }
    </div>
  `,
  styleUrls: ['./notification.component.scss'],
  standalone: true,
  imports: [],
})
export class NotificationStateComponent implements OnInit, OnDestroy {
  private notificationStateService = inject(NotificationStateService);
  private ngZone = inject(NgZone);
  private appRef = inject(ApplicationRef);

  // Use a separate array for display to avoid change detection issues
  readonly displayedNotifications = this.notificationStateService.notifications;
  private cleanup: EffectRef[] = [];
  private startTimes: Map<string, number> = new Map();
  private progressValues: Map<string, number> = new Map();
  private progressIntervalId: any = null;

  constructor() {
    // Keep progress bookkeeping aligned with the current notification signal.
    this.cleanup.push(
      effect(() => {
        const notifications = this.displayedNotifications();

        notifications.forEach(notification => {
          if (!this.startTimes.has(notification.id)) {
            this.startTimes.set(notification.id, Date.now());
            this.progressValues.set(notification.id, 0);
          }
        });

        const currentIds = new Set(notifications.map(notification => notification.id));
        Array.from(this.startTimes.keys()).forEach(id => {
          if (!currentIds.has(id)) {
            this.startTimes.delete(id);
            this.progressValues.delete(id);
          }
        });
      })
    );
  }

  // Method to get progress value from our local map instead of the notification object
  getProgressValue(notification: Notification): number {
    return this.progressValues.get(notification.id) || 0;
  }

  ngOnInit() {
    // Run progress updates outside Angular's change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.ngZone.runOutsideAngular(() => {
      this.progressIntervalId = setInterval(() => {
        let needsUpdate = false;

        this.displayedNotifications().forEach(notification => {
          if (notification.autoClose && notification.duration) {
            const startTime = this.startTimes.get(notification.id) || Date.now();
            const elapsedTime = Date.now() - startTime;
            const newProgressValue = Math.min((elapsedTime / notification.duration) * 100, 100);

            // Get current progress from our local map
            const currentProgress = this.progressValues.get(notification.id) || 0;

            // Only update if there's a meaningful change
            if (Math.abs(currentProgress - newProgressValue) > 0.5) {
              // Update our local map instead of the notification object
              this.progressValues.set(notification.id, newProgressValue);
              needsUpdate = true;
            }
          }
        });

        // Only trigger change detection if progress values have changed
        if (needsUpdate) {
          // Use ApplicationRef.tick() for more stable change detection
          this.ngZone.run(() => {
            this.appRef.tick();
          });
        }
      }, 100); // Update every 100ms
    });
  }

  remove(id: string) {
    this.notificationStateService.removeNotification(id);
  }

  ngOnDestroy() {
    // Clean up all effects
    this.cleanup.forEach(effectRef => effectRef.destroy());

    // Clear the interval
    if (this.progressIntervalId) {
      clearInterval(this.progressIntervalId);
    }
  }
}
